<!-- Metric_Alerts_Table.vue -->
<template>
  <div :style="{ height: autoHeight ? 'auto' : '500px', maxHeight: '500px', overflow: 'auto'  }">
    <AgGridVue
      :theme="standardListTheme"
      style="width: 100%; height: 100%"
      :column-defs="columnDefs"
      :row-data="rowData"
      :default-col-def="defaultColDef"
      :grid-options="gridOptions"
      :loading="loading"
      @grid-ready="onGridReady"
    />

    <FormUpdateObjectOwner
      v-if="showOwnerModal"
      :object-id="ownerModalData?.objectId"
      :content-type-model="ownerModalData?.objectContentTypeModel"
      :current-owner="ownerModalData?.ownerId"
      @close="onOwnerModalClose"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { AgGridVue } from 'ag-grid-vue3'
import { standardListTheme, formatCalendarDate } from '@/utils/common'
import CC_Owner_Badge from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Owner_Badge.vue"
import CC_Tag_Badge from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Tag_Badge.vue"
import CC_Router_Link_To_Entity from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Router_Link_To_Entity.vue"
import CC_Status_Icon from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Status_Icon.vue"
import FormUpdateObjectOwner from '@/components/forms/FormUpdateObjectOwner.vue'
import MetricAlertInstanceOverview from '@/components/Metric/Metric_Alert_Instance_Overview.vue'

export default {
  name: 'MetricAlertsTable',

  components: {
    AgGridVue,
    CC_Tag_Badge,
    CC_Owner_Badge,
    CC_Router_Link_To_Entity,
    CC_Status_Icon,
    FormUpdateObjectOwner,
    MetricAlertInstanceOverview
  },

  props: {
    // New entity-based props (following Engagement_Table pattern)
    entityId: {
      type: Number,
      required: false,
      default: null
    },
    metricId: {
      type: Number,
      required: false,
      default: null
    },
    performanceDomainId: {
      type: Number,
      required: false,
      default: null
    },
    includeDescendants: {
      type: Boolean,
      required: false,
      default: true
    },
    autoHeight: {
      type: Boolean,
      required: false,
      default: false
    },
    preLoadedData: {
      type: Array,
      required: false,
      default: null
    }
  },

  data() {
    return {
      gridApi: null,
      loading: true,
      defaultColDef: {
        flex: 1,
        sortable: true,
        filter: true,
        resizable: true,
        wrapText: true
      },
      rowData: [],
      showOwnerModal: false,
      ownerModalData: null,
      showAlertInstanceModal: false,
      alertInstanceModalData: null
    }
  },

  computed: {
    standardListTheme() {
      return standardListTheme
    },

    gridOptions() {
      return {
        domLayout: this.autoHeight ? 'autoHeight' : null,
        getRowId: (params) => {
          return `${params.data.alert || 'alert'}_${params.data.entity_id || 'entity'}_${params.data.id || 'row'}`;
        },
        getRowStyle: (params) => {
          const status = params.data?.status;
          const styles = {
            'active': { backgroundColor: 'rgba(254, 226, 226, 0.3)' },        // Very light red
            'acknowledged': { backgroundColor: 'rgba(219, 234, 254, 0.3)' },  // Very light blue
            'resolved': { backgroundColor: 'rgba(209, 250, 229, 0.3)' },      // Very light green
            'snoozed': { backgroundColor: 'rgba(254, 215, 170, 0.3)' },       // Very light orange
            'erroneous': { backgroundColor: 'rgba(243, 244, 246, 0.3)' },     // Very light gray
            'expired': { backgroundColor: 'rgba(229, 231, 235, 0.3)' }        // Very light medium gray
          };
          return styles[status] || {};
        }
      };
    },

    columnDefs() {
      const columns = [];

      columns.push(
        {
          headerName: 'Entity',
          field: 'entity_name',
          flex: 2,
          cellClass: 'sensitive cell-theme-left-aligned',
          cellRenderer: CC_Router_Link_To_Entity,
          cellRendererParams: {
            idField: 'entity_id',
            nameField: 'entity_name',
            iconField: 'entity_icon',
          }
        },
        {
          headerName: 'Alert',
          field: 'alert_name',
          cellClass: 'cell-theme-left-aligned',
          cellRenderer: (params) => {
            const alertName = params.value || 'Unknown Alert';
            const alertInstanceId = params.data?.id;

            if (!alertInstanceId) {
              const span = document.createElement('span');
              span.textContent = alertName;
              span.className = 'text-gray-900';
              return span;
            }

            const link = document.createElement('a');
            link.textContent = alertName;
            link.className = 'text-blue-600 hover:text-blue-800 hover:underline cursor-pointer';
            link.onclick = () => {
              this.$router.push({
                name: 'MetricAlertInstance',
                params: { alertInstanceId: alertInstanceId }
              });
            };
            return link;
          }
        },
        {
          headerName: 'Owner',
          headerTooltip: 'Alert Owner',
          cellClass: 'cell-theme-center-aligned justify-center',
          maxWidth: 100,
          field: 'assigned_to_name',
          valueGetter: (params) => {
            const firstName = params.data?.assigned_to_first_name
            const lastName = params.data?.assigned_to_last_name
            if (firstName && lastName) {
              return `${firstName} ${lastName}`
            } else if (firstName) {
              return firstName
            } else if (lastName) {
              return lastName
            }
            return null
          },
          cellRenderer: CC_Owner_Badge,
          // The name is drawn only by the badge (initials and a spoofed title);
          // the opt-in protects its filter and the spoofed list keeps it usable.
          context: { demoSensitive: true },
          cellRendererParams: {
            refreshData: this.refreshData,
            badge_type: 'alert_instance_owner',
            getOwnerData: (params) => {
              const firstName = params.data?.assigned_to_first_name
              const lastName = params.data?.assigned_to_last_name
              const fullName = firstName && lastName ? `${firstName} ${lastName}` : (firstName || lastName || null)
              return {
                ownerId: params.data?.assigned_to_id,
                ownerType: 'appuser',
                ownerName: fullName || '- -',
                ownerColor: params.data?.assigned_to_color || '#3B82F6',
                objectContentTypeModel: 'alertinstance',
                objectId: params.data?.id
              }
            },
            on: {
              'update-owner': (data) => {
                console.log('Update owner handler called with:', data);
                this.onOwnerModalOpen(data);
              }
            }
          },
        },
        {
          headerName: 'Affected Patients',
          cellClass: 'cell-theme-center-aligned justify-center',
          valueGetter: params => {
            const attribution = parseInt(params.data?.attribution) || 0
            const rate = parseFloat(params.data?.rate) || 0
            const direction = params.data?.detection_config?.direction || 'below'
            // Affected = patients who haven't received the intervention
            return Math.round(direction === 'below' ? attribution * (1 - rate) : attribution * rate)
          },
          tooltipValueGetter: params => {
            const attribution = params.data?.attribution || 0
            const rate = params.data?.rate || 0
            return `Attribution: ${attribution}\nRate: ${(rate * 100).toFixed(1)}%`
          }
        },
        {
          headerName: 'Total Impact',
          cellClass: 'cell-theme-center-aligned justify-center',
          sort: 'desc',
          sortIndex: 0,
          valueGetter: params => {
            const impactPerPatient = parseFloat(params.data?.impact_per_patient) || 0
            if (impactPerPatient === 0) return null
            const attribution = parseInt(params.data?.attribution) || 0
            const rate = parseFloat(params.data?.rate) || 0
            const direction = params.data?.detection_config?.direction || 'below'
            const affected = Math.round(direction === 'below' ? attribution * (1 - rate) : attribution * rate)
            return affected * impactPerPatient
          },
          valueFormatter: params => {
            if (params.value === null || params.value === undefined) return '—'
            return `$${params.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          },
          tooltipValueGetter: params => {
            const impactPerPatient = params.data?.impact_per_patient || 0
            if (!impactPerPatient) return 'No impact per patient configured'
            const attribution = parseInt(params.data?.attribution) || 0
            const rate = parseFloat(params.data?.rate) || 0
            const direction = params.data?.detection_config?.direction || 'below'
            const affected = Math.round(direction === 'below' ? attribution * (1 - rate) : attribution * rate)
            return `${affected} patients × $${impactPerPatient.toFixed(2)} per patient`
          }
        },
        {
          field: 'observation_date',
          headerName: 'Last Observed',
          cellClass: 'cell-theme-left-aligned',
          valueFormatter: params => {
            return formatCalendarDate(params.value, 'en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })
          },
        },
        {
          field: 'status',
          headerName: 'Status',
          maxWidth: 100,
          cellClass: 'cell-theme-center-aligned',
          cellRenderer: CC_Status_Icon
        },
        { field: 'id', hide: true },
        { field: 'entity_id', hide: true },
        { field: 'alert', hide: true },
        { field: 'assigned_to', hide: true },
        { field: 'attribution', hide: true },
        { field: 'rate', hide: true },
        { field: 'numerator', hide: true },
        { field: 'denominator', hide: true },
        { field: 'impact_per_patient', hide: true },
        { field: 'detection_config', hide: true }
      );

      return columns;
    }
  },

  watch: {
    entityId() {
      this.fetchData()
    },
    metricId() {
      this.fetchData()
    },
    performanceDomainId() {
      this.fetchData()
    },
    includeDescendants() {
      if (this.entityId) {
        this.fetchData()
      }
    },
    preLoadedData() {
      this.fetchData()
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize)
  },

  methods: {
    async onGridReady(params) {
      this.gridApi = params.api
      await this.fetchData()
      window.addEventListener('resize', this.onWindowResize)
    },

    async fetchData() {
      this.loading = true

      try {
        // Use preloaded data if available and no specific filters are applied
        if (this.preLoadedData && !this.entityId && !this.metricId && !this.performanceDomainId) {
          this.rowData = Array.isArray(this.preLoadedData) ? this.preLoadedData : []
          this.loading = false
          return
        }

        const params = {
          entity_id: this.entityId,
          include_descendants: this.includeDescendants,
          metric_id: this.metricId,
          performance_domain_id: this.performanceDomainId,
        };

        // Fetch directly from AlertInstance API
        const response = await axios.get('/api/impact_analytics/composable-alert-instances/', {
          params
        })

        // Handle paginated or direct array response
        const alertData = response.data?.results || response.data

        if (Array.isArray(alertData) && alertData.length > 0) {
          this.rowData = alertData
        } else {
          this.rowData = []
        }
      } catch (error) {
        console.error('Error fetching alert instances:', error)
        this.rowData = []
      } finally {
        this.loading = false
      }
    },

    onWindowResize() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit()
      }
    },

    async refreshData() {
      await this.fetchData()
      if (this.gridApi) {
        this.gridApi.refreshCells()
      }
    },

    onOwnerModalOpen(data) {
      console.log('Owner modal open called with data:', data);
      this.ownerModalData = data;
      this.showOwnerModal = true;
    },

    onOwnerModalClose(data) {
      this.showOwnerModal = false;
      if (data.refreshData) {
        this.refreshData();
      }
    },

    onAlertInstanceModalOpen(alertInstanceData) {
      console.log('Alert instance modal open called with data:', alertInstanceData);
      this.alertInstanceModalData = alertInstanceData;
      this.showAlertInstanceModal = true;
    },

    onAlertInstanceModalClose(data) {
      this.showAlertInstanceModal = false;
      // Only refresh if changes were made
      if (data && data.refreshNeeded) {
        this.refreshData();
      }
    }
  }
}
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
